import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type ApiError } from '@/lib/api';

/** A goal inside a KRA. Named `KraTemplate` server-side; the UI calls these "goals". */
export interface KraTemplate {
  id: string;
  name: string;
  description: string | null;
  weightage: number;
}

export interface KraGroup {
  id: string;
  name: string;
  description: string | null;
  departmentId: string | null;
  departmentName: string | null;
  /** Drives canModifyKraRecord client-side — the same rule the API rejects with. */
  createdById: string | null;
  createdByName: string | null;
  items: KraTemplate[];
}

export interface KraGroupInput {
  name: string;
  description?: string | null;
  departmentId?: string | null;
  items: Array<{ name: string; description?: string | null; weightage?: number }>;
}

/**
 * Every distinct goal across every KRA, so one can be reused rather than retyped.
 *
 * Deduplicated on the name because the same goal genuinely appears in several KRAs —
 * "Close 5 tickets a week" belongs to Support and to Implementation — and offering it
 * three times in a picker is three identical rows nobody can choose between.
 */
export function goalLibrary(kras: KraGroup[]): KraTemplate[] {
  const seen = new Map<string, KraTemplate>();
  for (const kra of kras) {
    for (const goal of kra.items) {
      const key = goal.name.trim().toLowerCase();
      if (!seen.has(key)) seen.set(key, goal);
    }
  }
  return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
}

const KEY = ['kra-groups'] as const;

export function useKraGroups(departmentId?: string) {
  return useQuery<KraGroup[], ApiError>({
    queryKey: [...KEY, departmentId ?? 'all'],
    queryFn: () =>
      api.get<KraGroup[]>(
        departmentId ? `/kra-groups?departmentId=${encodeURIComponent(departmentId)}` : '/kra-groups',
      ),
  });
}

function useGroupMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation<TResult, ApiError, TArgs>({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export const useCreateKraGroup = () =>
  useGroupMutation((body: KraGroupInput) => api.post<KraGroup>('/kra-groups', body));

export const useUpdateKraGroup = () =>
  useGroupMutation(({ id, ...body }: KraGroupInput & { id: string }) =>
    api.patch<KraGroup>(`/kra-groups/${id}`, body),
  );

export const useDeleteKraGroup = () =>
  useGroupMutation((id: string) => api.delete<void>(`/kra-groups/${id}`));

/**
 * Create a KRA for every designation that has not got one.
 *
 * Reports created and skipped separately rather than a total, so a second click reads as
 * "0 created, 44 already existed" instead of looking like it did the work twice.
 */
export const useCreateKrasFromDesignations = () =>
  useGroupMutation((_: void) =>
    api.post<{ created: number; skipped: number; names: string[] }>(
      '/kra-groups/from-designations',
    ),
  );

/**
 * Assigning returns what it did rather than a bare success, so the toast can say
 * "3 added, 2 already there" - the difference between the two is the whole reason
 * a second assign is safe.
 */
export function useAssignKraGroup(employeeId: string | 'me') {
  const queryClient = useQueryClient();
  return useMutation<{ added: number; skipped: number }, ApiError, { groupId: string; goalId: string }>({
    mutationFn: ({ groupId, goalId }) =>
      api.post<{ added: number; skipped: number }>(`/kra-groups/${groupId}/assign/${goalId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['goal-set', employeeId] }),
  });
}
